/**
 * Perfil e exclusão de conta.
 *
 * A exclusão pede que a pessoa **digite uma palavra**, não que confirme num
 * diálogo. Diálogo de confirmação é clicado no reflexo — a mesma mão que
 * apertou "excluir" aperta "sim" meio segundo depois. Digitar exige ler.
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { deleteAccount } from '@/features/identity/api';
import { useMe } from '@/features/identity/useMe';
import { useChangeStatus, useCoaches } from '@/features/relationships/useRelationships';
import { signOut } from '@/features/auth/useSession';

/**
 * Quem acompanha esta pessoa — e o botão de encerrar.
 *
 * Fica no perfil, e não numa tela própria, porque a lista tem no máximo dois ou
 * três itens no P0. O que **não** pode faltar é o botão: quem aceitou um
 * convite precisa de um lugar óbvio para desfazer, e encerrar é a única
 * mudança de estado que o aluno pode fazer.
 */
function Coaches() {
  const { t } = useTranslation(['relationships']);
  const { data } = useCoaches();
  const change = useChangeStatus();

  const live = (data ?? []).filter((link) => link.status === 'active' || link.status === 'paused');
  if (live.length === 0) return null;

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
        {t('relationships:coaches.title')}
      </h2>
      <ul className="flex flex-col gap-2">
        {live.map((link) => (
          <li key={link.relationship_id} className="mh-card flex items-center justify-between">
            <span>
              <span className="block font-medium text-(--color-sand-900)">
                {link.counterpart_name}
              </span>
              <span className="block text-sm text-(--color-sand-700)">
                {t(`relationships:status.${link.status}`)}
              </span>
            </span>
            <button
              type="button"
              className="mh-button mh-button--ghost"
              disabled={change.isPending}
              onClick={() =>
                change.mutate({ relationshipId: link.relationship_id, status: 'ended' })
              }
            >
              {t('relationships:coaches.end')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ProfilePage() {
  const { t } = useTranslation(['common', 'errors']);
  const { data: me } = useMe();

  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [failed, setFailed] = useState(false);

  const confirmWord = t('common:profile.deleteConfirmWord');
  const matches = typed.trim().toUpperCase() === confirmWord;

  const mutation = useMutation({
    mutationFn: deleteAccount,
    // A conta do GoTrue já não existe; encerrar a sessão local é o que
    // devolve a pessoa a um estado coerente em vez de um token órfão.
    onSuccess: () => signOut(),
    onError: () => setFailed(true),
  });

  return (
    <div className="flex flex-col gap-5 px-5 py-6">
      <h1 className="text-3xl">{t('common:profile.title')}</h1>

      {me ? (
        <section className="mh-card">
          <p className="text-xl font-semibold">{me.display_name}</p>
          {/* Os papéis por nome próprio. A versão anterior reaproveitava os
              rótulos da navegação ("Hoje", "Plano") e passou a mentir quando
              `admin` entrou: qualquer papel que não fosse aluno virava "Plano". */}
          <p className="mt-1 text-sm text-(--color-sand-700)">
            {me.roles.map((role) => t(`common:role.${role}`)).join(' · ')}
          </p>
        </section>
      ) : null}

      {/* Só aparece o perfil do papel que a pessoa tem. Mostrar os dois e
          recusar depois seria oferecer uma tela que não leva a lugar nenhum. */}
      <nav className="flex flex-col gap-3">
        {me?.roles.includes('student') ? (
          <Link to="/profile/student" className="mh-button mh-button--secondary text-center">
            {t('common:profile.studentProfile')}
          </Link>
        ) : null}
        {me?.roles.includes('professional') ? (
          <>
            <Link to="/profile/professional" className="mh-button mh-button--secondary text-center">
              {t('common:profile.professionalProfile')}
            </Link>
            <Link to="/students" className="mh-button mh-button--secondary text-center">
              {t('common:profile.students')}
            </Link>
            <Link to="/templates" className="mh-button mh-button--secondary text-center">
              {t('common:profile.templates')}
            </Link>
          </>
        ) : null}
        {/* Curadoria só para quem tem o papel. Não é segurança — a API recusa
            de qualquer forma —, é não mostrar uma porta que não abre. */}
        {me?.roles.includes('admin') ? (
          <Link to="/curation" className="mh-button mh-button--secondary text-center">
            {t('common:profile.curation')}
          </Link>
        ) : null}
      </nav>

      <Coaches />

      <button type="button" onClick={signOut} className="mh-button mh-button--secondary">
        {t('common:profile.signOut')}
      </button>

      <section className="mt-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-(--color-sand-500)">
          {t('common:profile.danger')}
        </h2>

        <div className="mh-card">
          <p className="text-sm text-(--color-sand-700)">{t('common:profile.deleteWarning')}</p>

          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="mh-button mt-4 w-full border-2 border-(--color-danger) bg-transparent text-(--color-danger)"
            >
              {t('common:profile.deleteAccount')}
            </button>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              <div className="mh-field">
                <label htmlFor="confirm-delete">{t('common:profile.deleteConfirm')}</label>
                <input
                  id="confirm-delete"
                  type="text"
                  autoComplete="off"
                  autoFocus
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                />
              </div>

              {failed ? (
                <Alert tone="error" live="assertive">
                  {t('common:profile.deleteFailed')}
                </Alert>
              ) : null}

              <div className="flex gap-3">
                <button
                  type="button"
                  className="mh-button mh-button--ghost"
                  onClick={() => {
                    setConfirming(false);
                    setTyped('');
                    setFailed(false);
                  }}
                >
                  {t('common:profile.deleteCancel')}
                </button>
                <button
                  type="button"
                  disabled={!matches || mutation.isPending}
                  onClick={() => mutation.mutate()}
                  className="mh-button flex-1 bg-(--color-danger) text-(--color-sand-0)"
                >
                  {mutation.isPending
                    ? t('common:state.loading')
                    : t('common:profile.deleteSubmit')}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
